import CloseIcon from '@mui/icons-material/Close'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Slide,
  SlideProps,
  Snackbar,
  Typography,
} from '@mui/material'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />
}

/**
 * Lógica principal del PWA separada para no romper las reglas de los hooks
 */
const PwaPromptLogic: FC = () => {
  const [open, setOpen] = useState(false)

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_, registration) {
      if (!registration) return

      const intervalMS = 15 * 60 * 1000 // Busca actualización cada 15 minutos
      setInterval(() => {
        registration.update().catch((error) => {
          console.warn('No se pudo comprobar la actualización de la PWA:', error)
        })
      }, intervalMS)
    },
  })

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && 'serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration()
          if (registration) {
            await registration.update()
          }
        } catch (error) {
          console.warn('Error al actualizar SW on visibilitychange:', error)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  useEffect(() => {
    if (needRefresh) {
      setOpen(true)
    }
  }, [needRefresh])

  const handleDismiss = useCallback(() => {
    setOpen(false)
    setNeedRefresh(false)
  }, [setNeedRefresh])

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      slots={{
        transition: SlideTransition,
      }}
      sx={{
        top: { xs: 8, sm: 24 },
        width: { xs: '100%', sm: 'auto' },
        px: { xs: 2, sm: 0 },
      }}
    >
      <Paper
        elevation={4}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2.5 },
          p: 1.5,
          pl: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          width: '100%',
          maxWidth: 650,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: '1 1 280px' }}>
          <Box sx={{ display: 'flex', color: 'primary.main' }}>
            <SystemUpdateAltIcon sx={{ fontSize: 36 }} />
          </Box>
          <Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: 'text.primary' }}
              gutterBottom
            >
              Nueva versión disponible
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}
            >
              Haga clic en el botón actualizar para recargar el sistema.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: '1 1 auto',
            justifyContent: { xs: 'flex-end', sm: 'flex-end' },
          }}
        >
          <Button
            variant="contained"
            fullWidth
            color="warning"
            onClick={() => updateServiceWorker(true)}
            sx={{
              textTransform: 'none',
              px: 2.5,
              py: 0.75,
              borderRadius: 1.5,
              fontWeight: 600,
            }}
          >
            Actualizar
          </Button>
          <IconButton
            size="small"
            onClick={handleDismiss}
            title="Actualizar mas tarde"
            aria-label="Cerrar notificación"
            sx={{ color: 'text.secondary', bgcolor: 'action.hover' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Snackbar>
  )
}

/**
 * Banner flotante para actualización de aplicación
 * @author isi-template
 */
const ReloadPrompt: FC = () => {
  const isDev = import.meta.env.DEV

  // El hook siempre se llama, en el mismo orden, en todo render
  useEffect(() => {
    if (!isDev) return

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister().then(() => {
            console.log('Service Worker residual eliminado en entorno de desarrollo.')
          })
        }
      })
    }
  }, [isDev])

  // El return condicional va DESPUÉS de todos los hooks, eso sí está permitido
  if (isDev) {
    return null
  }

  // Si es producción, montamos la lógica real
  return <PwaPromptLogic />
}

export default ReloadPrompt
